import React, { useState, useEffect, useRef } from 'react';
import { ThreeYearChildQuestions, FiveYearChildQuestions } from './questions';
import { FiCheckCircle, FiMail } from 'react-icons/fi';
import DragAndDrop from '../../components/DragAndDrop';
import VoiceRecorder from '../../components/VoiceRecorder';
import { useLocation } from 'react-router-dom';
import { BsWhatsapp } from 'react-icons/bs';

interface Option {
    option_text: string;
    option_weight: number;
}

export interface Question {
    survey_title: string,
    question_text: string;
    question_type: string;
    question_order: number;
    question_weight: number;
    option_type: string;
    images: string[]
    options: Option[];
}

export interface Answers {
    [key: number]: string;
}

const sampleAnswers = {
    "0": "Yes",
    "1": "No",
    "2": "Yes",
    "3": "No",
    "4": "No",
    "5": "Yes",
    "6": "Yes",
    "7": "Yes",
    "8": "No",
    "9": "Yes",
    "10": "No",
    "11": "No",
    "12": "No",
    "13": "No",
    "14": "No",
    "15": "No",
    "16": "No",
    "17": "No",
    "18": "No",
    "19": "No",
    "20": "No",
    "21": "No",
    "22": "No",
    "23": "No",
    "24": "No",
    "25": "No",
    "26": "No",
    "27": "Yes",
    "28": "Yes",
    "29": "Yes",
    "30": "Yes",
    "31": "Yes",
    "32": "No",
    "33": "No",
    "34": "No",
    "35": "No",
    "36": "No",
    "37": "No",
    "38": "Yes",
    "39": "Yes",
    "40": "No",
    "41": "Yes",
    "42": "No",
    "43": "No",
    "44": "No",
    "45": "No",
    "46": "No",
    "47": "No",
    "48": "No",
    "49": "No",
    "50": "No"
}

const QUESTIONS_PER_PAGE = 10;

const ChildSurvey: React.FC = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const urlEmail = queryParams.get('email');
    const [email, setEmail] = useState(urlEmail);
    const [emailError, setEmailError] = useState('');
    const [details, setDetails] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [questions, setQuestions] = useState<Question[]>([]);   
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [answers, setAnswers] = useState<Answers>({});
    const [surveyAnswers, setSurveyAnswers] = useState<Answers>({});
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState(false);
    const isMobile = window.innerWidth < 460;
    const [showPreview, setShowPreview] = useState(false);
    const largeImg = [33, 37, 42,44, 45]
    const [showInitialPopup, setShowInitialPopup] = useState(true);
    const bankDetails = {
        Account_Holder_Name : 'Jayalakshmi Natarajan',
        Account_number : 923010036428752,
        IFSC_Code : 'UTIBOOO1511',
        Bank_Address: '27, Paper Mills Road, Chennai-600 011, Tamilnadu, India'
    }    

    useEffect(() => {
        try {
            if (details) {
                const ageQuestions = details === '3' ? ThreeYearChildQuestions : [...ThreeYearChildQuestions, ...FiveYearChildQuestions];
                setQuestions([...ageQuestions]);
            }
            setIsLoading(false);
        } catch (err) {
            setError('Failed to Loading questions');
            setIsLoading(false);
        }
    }, [details]);

    const questionRefs = useRef<(HTMLDivElement | null)[]>([]);

    const handleAnswer = (questionIndex: number, answer: string): void => {
        const question = questions[questionIndex];
        const isUploadQuestion = question.question_text.includes('[WRITE]') || question.question_text.includes('[REC]');

        setAnswers(prev => ({
            ...prev,
            [questionIndex]: answer
        }));

        if(!isUploadQuestion || answer.toLowerCase() !== 'yes') {
            setSurveyAnswers(prev => ({
                ...prev,
                [questionIndex + 1]: answer
            }));

            scroller(questionIndex);
        }
    };

    const scroller = (questionIndex: number) => {
        const nextQuestionIndex = questionIndex + 1;
        const nextQuestionRef = questionRefs.current[nextQuestionIndex];

        if (nextQuestionRef && nextQuestionIndex < (currentPage + 1) * QUESTIONS_PER_PAGE) {
            setTimeout(() => {
                nextQuestionRef.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }, 100);
        }
    };

    const getCurrentPageQuestions = (): Question[] => {
        const startIndex = currentPage * QUESTIONS_PER_PAGE;
        return questions.slice(startIndex, startIndex + QUESTIONS_PER_PAGE);
    };

    const getProgressPercentage = (): number => {
        const totalAnswered = Object.keys(answers).length;
        return questions.length ? (totalAnswered / questions.length) * 100 : 0;
    };

    const handleNextPage = () => {
        if ((currentPage + 1) * QUESTIONS_PER_PAGE < questions.length) {
            setCurrentPage(prev => prev + 1);
            window.scrollTo(0, 0);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 0) {
            setCurrentPage(prev => prev - 1);
            window.scrollTo(0, 0);
        }
    };

    const isPageComplete = (): boolean => {
        const currentQuestions = getCurrentPageQuestions();
        return currentQuestions.every((_, index) => {
            const globalIndex = currentPage * QUESTIONS_PER_PAGE + index;
            const hasAnswer = answers[globalIndex] !== undefined;
            return hasAnswer && hasRequiredUpload(globalIndex);
        });
    };

    const isFinalPage = (): boolean => {
        return (currentPage + 1) * QUESTIONS_PER_PAGE >= questions.length;
    };

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                <div className="text-gray-600">{error}</div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="fixed inset-0 bg-white bg-opacity-70 flex items-center justify-center p-4">
                <div className={`bg-white rounded-lg w-full max-w-xl p-6 flex flex-col gap-3 justify-center`}>
                    <div className='flex flex-col gap-3 justify-center items-center'>
                        <FiCheckCircle className='w-10 h-10 text-green-500' />
                        <p>Thanks for submitting the Detailed Assessment.</p>

                        <p className="text-lg text-center font-bold">
                            Please make the payment of USD 30/GBP 25/AED 100/40 SGD/28 EUR/100 QAR/100 SAR or equivalent to the following account and send the payment receipt or screenshot to 
                            <a href="mailto:info@shvesaminds.com" className="text-blue-500 inline-flex items-center space-x-1">
                                <FiMail /> <span className='pt-1'>info@shvesaminds.com</span>
                            </a> or
                            <a href="https://wa.me/+916374480026" className="text-green-500 inline-flex items-center space-x-1 pl-1">
                                <BsWhatsapp /> <span className='pt-1'>WhatsApp</span>
                            </a>
                        </p>
                    </div>
                    
                    {/* Bank Details Section */}
                    <div>
                        <h4 className="font-semibold text-left text-lg">Bank Details</h4>
                        <p><b>Account Holder:</b> {bankDetails.Account_Holder_Name}</p>
                        <p><b>Account Number:</b> {bankDetails.Account_number}</p>
                        <p><b>IFSC Code:</b> {bankDetails.IFSC_Code}</p>
                        <p><b>Bank Address:</b> {bankDetails.Bank_Address}</p>
                    </div>
        
                    {/* Instructions Section */}
                    <div className="flex flex-col gap-4 text-center items-center">
                        <p>We recommend to complete the free Assessment as well</p>
                        <p>You will also receive the same message in your email.</p>
                        <div className='w-full flex justify-around items-center'>
                            <button
                                onClick={() => window.location.href = "mailto:info@shvesaminds.com"}
                                className="border border-gray-200 shadow-md p-3 rounded-lg flex items-center gap-2"
                            >
                                <FiMail className="w-5 h-5 text-gray-700" />
                                Mail Screenshot
                            </button>

                            <button
                                onClick={() => window.location.href = "survey"}
                                className="bg-green-500 text-white font-semibold shadow-md p-3 rounded-lg flex items-center"
                            >
                                Begin Assessment
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }   

    if (isLoading) {
        return (
            <div className="min-h-screen w-full flex flex-col items-center justify-center p-4 space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-green-500"></div>
                <div className="text-gray-600">{questions ? 'Submitting Survey...' : 'Loading questions...'}</div>
            </div>
        );
    }

    const currentQuestions = getCurrentPageQuestions();

    const handleFileUrl = (url: string, questionIndex: number) => {
        setSurveyAnswers(prev => ({
            ...prev,
            [questionIndex + 1]: url
        }));

        scroller(questionIndex);
    };

    const removeFileUrl = (questionIndex: number) => {
        setSurveyAnswers(prev => {
            const newAnswers = { ...prev };
            delete newAnswers[questionIndex + 1];
            return newAnswers;
        });
    };

    const handleAudioUrl = (url: string, questionIndex: number) => {
        setSurveyAnswers(prev => ({
            ...prev,
            [questionIndex + 1]: url
        }));

        scroller(questionIndex);
    }

    const removeAudioUrl = (questionIndex: number) => {
        setSurveyAnswers(prev => {
            const newAnswers = { ...prev };
            delete newAnswers[questionIndex + 1];
            return newAnswers;
        });
    }

    const isUploadRequired = (questionIndex: number): boolean => {
        const question = questions[questionIndex];
        if (!question) return false;
        
        const isUploadQuestion = question.question_text.includes('[WRITE]') || question.question_text.includes('[REC]');
        const selectedWeight = answers[questionIndex];
        
        return isUploadQuestion && selectedWeight === 'Yes';
    };

    const hasRequiredUpload = (questionIndex: number): boolean => {
        if (!isUploadRequired(questionIndex)) return true;
        
        const question = questions[questionIndex];
        if (question.question_text.includes('[WRITE]')) {
            return !!answers[questionIndex];
        } else if (question.question_text.includes('[REC]')) {
            return !!answers[questionIndex];
        }
        return true;
    };

    const handleSubmitSurvey = async () => {
        setIsLoading(true)
        const surveyData = prepareSurveyData(surveyAnswers, questions);
        try {
            const response = await fetch('https://e6w2t1d2f1.execute-api.us-east-1.amazonaws.com/dev/survey', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(surveyData)
            });
        
            if (!response.ok) {
                throw new Error(`Failed to submit survey: ${response.statusText}`);
            }
            setIsLoading(false)
            setSuccess(true);
        
        } catch (error: any) {
            setIsLoading(false)
            console.error('Error submitting survey:', error);
            if (error instanceof TypeError && error.message === 'Failed to fetch') {
                setError('Network or CORS issue detected');
            } else {
                setError(error.response.data.message);
            }
        }
    };

    const prepareSurveyData = (answers:Answers, questions:Question[]) => {
        const surveyResponse = {
            userDetails:{
                email
            },
            advanceresponses: questions.map(question => {
                const answer = answers[question.question_order]
                return {
                    questionId: question.question_order,
                    questionText: question.question_text,
                    questionType: question.question_type,
                    selectedAnswer: answer? answer : 'No' ,
                    selectedWeight: question.options.find(
                        opt => opt.option_text === answers[question.question_order]
                    )?.option_weight || 0
                };
            })
        };

        return surveyResponse;
    };

    if (showPreview) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto">
                    <div className="bg-white rounded-2xl shadow-xl px-6 py-6">
                        <img className="mx-auto h-20 my-6 w-auto" src='/newLogo.png' alt="Shvesa Logo" />
                        <h2 className="text-2xl font-semibold text-gray-800 mb-6">Survey Preview</h2>
                        
                        <div className="space-y-6">
                            {questions.map((question, index) => (
                                <div key={index} className="border-b border-gray-200 last:border-0 pb-4">
                                    <p className="font-medium text-gray-700 mb-2">
                                        {index + 1}. {question.question_text}
                                    </p>
                                    {surveyAnswers[index + 1] ? (
                                        surveyAnswers[index + 1].includes('http') ? (
                                            <div className="flex items-center gap-4">
                                                <p className="text-gray-600">Answer: </p>
                                                {question.question_text.includes('[REC]') ? (
                                                    <audio src={surveyAnswers[index + 1]} controls />
                                                ) : (
                                                    <img
                                                        src={surveyAnswers[index + 1]}
                                                        alt={`Question ${index + 1} image`}
                                                        className="w-40 h-40 object-fill rounded-lg shadow-md"
                                                    />
                                                )}
                                            </div>
                                        ) : (
                                            <p className="text-gray-600 pl-6">
                                                Answer: {surveyAnswers[index + 1]}
                                            </p>
                                        )
                                    ) : (
                                        <p className="text-gray-600 pl-6">Answer: No</p>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-end gap-4 mt-8">
                            <button
                                onClick={() => setShowPreview(false)}
                                className="px-4 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100"
                            >
                                Edit Responses
                            </button>
                            <button
                                onClick={handleSubmitSurvey}
                                className="px-6 py-2 rounded-lg font-medium bg-gradient-to-r from-green-500 to-green-600 
                                    text-white hover:from-green-600 hover:to-green-700"
                            >
                                Confirm & Continue
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        );
    }

    const validateEmail = (email: string): boolean => {
        if (!email) {
            setEmailError('Email is required');
            return false;
        }
        
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const isValid = emailRegex.test(email);
        
        if (!isValid) {
            setEmailError('Please enter a valid Gmail, Yahoo, or Outlook email address');
            return false;
        }
        
        setEmailError('');
        return true;
    };

    const handleContinue = (): boolean => {
        if (!urlEmail) {
            if (!validateEmail(email || '')) {
                return false;
            }
        }
        
        if (!details) {
            return false;
        }

        setEmailError('');
        setShowInitialPopup(false);
        return true;
    };

    if (showInitialPopup) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto relative">
                    <h1 className="text-2xl text-center font-semibold mb-4">Detailed Assessment</h1>

                    <p className="text-lg text-center font-semibold mb-4">Thank you for choosing our detailed assessment.</p>

                    <p className='text-xs text-center'>This test is designed by our team of experts to provide accurate insights in ASD signs and Symptoms. The test results will be analysed by our team and the detailed assessment result will be sent to you within 3 working days from the date of completion of test. This test requires voice recordings of the child so please keep your child ready while starting the test. After completion of the test you will be asked to make a payment of USD 30 or equivalent.</p>

                    <div className="space-y-4 mt-4">
                        {!urlEmail && (
                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2">
                                    Email:
                                </label>
                                <input
                                    type="email"
                                    value={email || ''}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        // Clear error when user starts typing
                                        if (emailError) setEmailError('');
                                    }}
                                    className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                                        emailError ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="Enter your email"
                                />
                                {emailError && (
                                    <p className="text-red-500 text-sm mt-1">{emailError}</p>
                                )}
                            </div>
                        )}
                        
                        <label htmlFor="age-select" className="block text-gray-700 font-medium mb-2">
                            Select Child's Age:
                        </label>
                        <select 
                            id="age-select"
                            value={details || ''}
                            onChange={(e) => setDetails(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                            <option value="" disabled>
                                -- Choose an age --
                            </option>
                            <option value="3">less than 5 years</option>
                            <option value="5">5 years old and above</option>
                        </select>

                        <button
                            onClick={handleContinue}
                            className={`w-full mt-4 py-2 rounded-md font-medium transition-all duration-300 
                                ${(!details || (!urlEmail && !email)) 
                                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                                    : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'}`}
                            disabled={!details || (!urlEmail && !email)}
                        >
                            Continue
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                <div className="bg-white rounded-2xl shadow-xl px-6 py-6 relative overflow-hidden">
                    <img className="mx-auto h-20 my-6 w-auto" src='/newLogo.png' alt="Shvesa Logo" />

                    {/* Progress bar */}
                    <div className="h-2 bg-gray-100 rounded-full mb-8">
                        <div 
                            className="h-full bg-gradient-to-r from-green-400 to-green-500 rounded-full transition-all duration-500 ease-in-out"
                            style={{ width: `${getProgressPercentage()}%` }}
                        />
                    </div>

                    {/* Page counter */}
                    <div className="text-sm text-gray-500 mb-6">
                        Page {currentPage + 1} of {Math.ceil(questions.length / QUESTIONS_PER_PAGE)}
                    </div>

                    {/* Questions */}
                    <div className="space-y-8">
                        {currentQuestions.map((question, pageIndex) => {
                            const globalIndex = currentPage * QUESTIONS_PER_PAGE + pageIndex;
                            const showUploadOption = isUploadRequired(globalIndex);

                            return (
                                <div 
                                    key={globalIndex} 
                                    ref={el => questionRefs.current[globalIndex] = el}
                                    className="pb-8 border-b border-gray-200 last:border-0"
                                >
                                    <h2 className={`${isMobile ? 'text-sm' : 'text-xl'} font-medium text-gray-900 mb-6`}>
                                        {globalIndex + 1}. {question.question_text}
                                    </h2>

                                    {/* Images */}
                                    {question.images && question.images.length > 0 && (
                                        <div className={`grid ${question.images.length > 1 ? 'grid-cols-3' : 'grid-cols-1'} gap-2 mb-6`}>
                                            {question.images.map((image, index) => (
                                                <div key={index} className="flex justify-center items-center">
                                                    <img
                                                        src={image}
                                                        alt={`Question ${globalIndex + 1} image ${index + 1}`}
                                                        className={`${question.images.length > 1 ? 'w-32 h-32' : details === '5' && largeImg.includes(globalIndex + 1)? '' : 'w-40 h-40'} object-fill rounded-lg shadow-md`}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Options */}
                                    <div className="space-y-3">
                                        {question.options.map((option, optionIndex) => (
                                            <button
                                                key={optionIndex}
                                                onClick={() => {
                                                    handleAnswer(globalIndex, option.option_text);
                                                    if (option.option_weight !== 1) {
                                                        if (question.question_text.includes('[WRITE]')) {
                                                            removeFileUrl(globalIndex);
                                                        } else if (question.question_text.includes('[REC]')) {
                                                            removeAudioUrl(globalIndex);
                                                        }
                                                    }
                                                }}
                                                className={`w-full text-left rounded-xl transition-all duration-200 
                                                    ${isMobile ? 'text-sm p-3' : 'text-base p-4'}
                                                    ${answers[globalIndex] === option.option_text 
                                                        ? 'bg-gradient-to-r from-green-50 to-green-100 border-green-200' 
                                                        : 'bg-white hover:bg-gray-50'} 
                                                    border-2 
                                                    ${answers[globalIndex] === option.option_text 
                                                        ? 'border-green-300' 
                                                        : 'border-gray-200'}
                                                    transform hover:scale-[1.01] active:scale-[0.99]`}
                                            >
                                                <div className="flex items-center">
                                                    <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center
                                                        ${answers[globalIndex] === option.option_text 
                                                            ? 'border-green-500' 
                                                            : 'border-gray-300'}`}
                                                    >
                                                        {answers[globalIndex] === option.option_text && (
                                                            <div className="w-3 h-3 rounded-full bg-green-500" />
                                                        )}
                                                    </div>
                                                    <span className={`font-medium ${
                                                        answers[globalIndex] === option.option_text 
                                                            ? 'text-green-700' 
                                                            : 'text-gray-700'
                                                    }`}>
                                                        {option.option_text}
                                                    </span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>

                                    {/* Conditional Upload/Record Components */}
                                    {showUploadOption && (
                                        <div className="mt-4">
                                            {question.question_text.includes('[REC]') ? (
                                                <VoiceRecorder 
                                                    onRecordingComplete={(url) => handleAudioUrl(url, globalIndex)} 
                                                    onRemoveRecording={() => removeAudioUrl(globalIndex)}
                                                />
                                            ) : question.question_text.includes('[WRITE]') ? (
                                                <DragAndDrop 
                                                    onFilesUpload={(url) => handleFileUrl(url, globalIndex)}
                                                    onRemove={() => removeFileUrl(globalIndex)}
                                                    currentFile={surveyAnswers[globalIndex + 1]}
                                                />
                                            ) : null}
                                            {!hasRequiredUpload(globalIndex) && (
                                                <p className="text-red-500 mt-2 text-sm">
                                                    Please {question.question_text.includes('[REC]') ? 'record audio' : 'upload a file'} to continue
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Navigation */}
                    <div className="flex justify-between mt-8">
                        <button
                            onClick={handlePreviousPage}
                            disabled={currentPage === 0}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors
                                ${currentPage === 0 
                                    ? 'text-gray-400 cursor-not-allowed' 
                                    : 'text-gray-600 hover:text-gray-800'}`}
                        >
                            Previous Page
                        </button>
                        {isFinalPage() ? (
                            <button
                                disabled={!isPageComplete()}
                                onClick={() => {setShowPreview(true); window.scrollTo(0, 0);}}
                                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl
                                    ${isPageComplete()
                                        ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                            >
                                Review Answers
                            </button>
                        ) : (
                            <button
                                onClick={handleNextPage}
                                disabled={!isPageComplete()}
                                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl
                                    ${isPageComplete()
                                        ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                            >
                                Next Page
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChildSurvey;